"use client";

import { forwardRef, type Ref } from "react";
import { Container } from "@/components/ui/container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShopCard } from "./ShopCard";
import { InfiniteScrollTrigger } from "./InfiniteScrollTrigger";
import type { PublicCommunityShopWithProducts } from "@/lib/community/types";

interface CommunitySearchResultsProps {
  shops: PublicCommunityShopWithProducts[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
  onRetry: () => void;
  onClearFilters: () => void;
  returnTo?: string;
}

export const CommunitySearchResults = forwardRef(
  function CommunitySearchResultsInner(
    {
      shops,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      error,
      onRetry,
      onClearFilters,
      returnTo,
    }: CommunitySearchResultsProps,
    ref: Ref<HTMLDivElement>,
  ) {
    return (
      <div ref={ref}>
        <Container size="xl">
          {error && !isLoading && shops.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted text-lg">
                Failed to load results. Please try again.
              </p>
              <Button variant="secondary" onClick={onRetry} className="mt-4">
                Try again
              </Button>
            </div>
          ) : shops.length === 0 && !isLoading ? (
            <div className="text-center py-16">
              <p className="text-muted text-lg">No suppliers found.</p>
              <Button variant="secondary" onClick={onClearFilters} className="mt-4">
                Clear search and filters
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {shops.map((shop) => (
                  <ShopCard key={shop.id} shop={shop} returnTo={returnTo} />
                ))}
                {(isLoading || isFetchingNextPage) &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <Card
                      key={`skeleton-${i}`}
                      className="h-64 animate-pulse bg-raised/70"
                    />
                  ))}
              </div>
              {error && (
                <p role="status" className="mt-6 text-center text-muted">
                  Could not load more brands. Your results are still here.
                </p>
              )}
              <InfiniteScrollTrigger
                hasNextPage={hasNextPage || !!error}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={error ? onRetry : fetchNextPage}
                autoLoad={!error}
                label={error ? "Try again" : "Load more"}
              />
            </>
          )}
        </Container>
      </div>
    );
  },
);
